import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Clock, DollarSign, Users, Trophy,
  ChevronRight, AlertTriangle, Check, Flame, Star,
  Gift, TrendingUp, Target,
} from 'lucide-react';

// ─── Flash Bounties ─────────────────────────────────────────────────
// Dynamic mid-shift challenges. The kitchen is the game master.
// Real cash. Real urgency. Real fun.

interface Bounty {
  id: string;
  type: 'flash' | 'kitchen' | 'team' | 'personal' | 'revenue';
  status: 'live' | 'claimed' | 'expired' | 'upcoming';
  title: string;
  description: string;
  reward: string;
  rewardType: 'instant_cash' | 'bonus_pool' | 'revenue_share';
  timeLeft?: string;
  claimedBy?: string;
  progress?: { current: number; target: number };
  script?: string;
}

const BOUNTIES: Bounty[] = [
  {
    id: '1', type: 'kitchen', status: 'live', title: 'Halibut Hustle',
    description: 'Kitchen has 8 halibut portions to move before close. Help them out.',
    reward: '$10 per sale', rewardType: 'instant_cash', timeLeft: '42 min',
    script: '"The halibut is incredible tonight — the chef just told me it\'s the freshest we\'ve had all week. You\'ll love it."',
  },
  {
    id: '2', type: 'flash', status: 'live', title: 'Ribeye Rush',
    description: 'First server to sell a ribeye gets the bounty. Race is on.',
    reward: '$15 instant', rewardType: 'instant_cash', timeLeft: '28 min',
    script: '"If you\'re a steak person at all, our ribeye is the move. It\'s a 14oz dry-aged cut — I had it last week and I\'m still thinking about it."',
  },
  {
    id: '3', type: 'revenue', status: 'live', title: 'Wine Night',
    description: 'Every bottle of wine you sell tonight, you get 5% of the bottle price. Sell the $60 Cab? That\'s $3 extra in your pocket.',
    reward: '5% per bottle', rewardType: 'revenue_share', timeLeft: 'All night',
    progress: { current: 2, target: 0 },
    script: '"Can I suggest a wine for the table? The 2019 Cabernet is phenomenal with what you ordered — want me to bring it over?"',
  },
  {
    id: '4', type: 'team', status: 'live', title: 'Happy Hour Blitz',
    description: 'Team sells 20 cocktails before 7 PM. Everyone gets the bonus.',
    reward: '$5 each', rewardType: 'bonus_pool', timeLeft: '1h 15m',
    progress: { current: 14, target: 20 },
  },
  {
    id: '5', type: 'personal', status: 'live', title: 'Dessert Sweep',
    description: 'Sell dessert to 3 different tables tonight.',
    reward: '$8 bonus', rewardType: 'instant_cash', timeLeft: 'All shift',
    progress: { current: 1, target: 3 },
    script: '"Before I bring the check — have you seen our Dubai Chocolate? It\'s this pistachio-filled chocolate thing and it\'s honestly insane. Want me to bring one for the table?"',
  },
  {
    id: '6', type: 'flash', status: 'claimed', title: 'Speed Sell',
    description: 'First server to upsell an appetizer this hour.',
    reward: '$5 instant', rewardType: 'instant_cash', claimedBy: 'Sarah C.',
  },
  {
    id: '7', type: 'kitchen', status: 'expired', title: 'Soup Season',
    description: 'Move 10 bowls of the lobster bisque.',
    reward: '$3 per bowl', rewardType: 'instant_cash',
  },
  {
    id: '8', type: 'revenue', status: 'upcoming', title: 'Weekend Warrior',
    description: 'This weekend: earn 3% of every premium entree you sell (Wagyu, Ribeye, Halibut). Top seller gets an extra $50.',
    reward: '3% + $50 top prize', rewardType: 'revenue_share', timeLeft: 'Starts Saturday',
  },
];

const typeConfig: Record<string, { label: string; color: string; bg: string }> = {
  flash: { label: 'FLASH', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  kitchen: { label: 'KITCHEN', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  team: { label: 'TEAM', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  personal: { label: 'PERSONAL', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  revenue: { label: 'REV SHARE', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
};

const rewardBadge: Record<string, { label: string; color: string }> = {
  instant_cash: { label: 'Instant Cash', color: 'text-emerald-400' },
  bonus_pool: { label: 'Team Bonus', color: 'text-blue-400' },
  revenue_share: { label: 'Revenue Share', color: 'text-amber-400' },
};

const FlashBounties: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'live' | 'revenue'>('all');

  const liveBounties = BOUNTIES.filter(b => b.status === 'live');
  const revenueBounties = BOUNTIES.filter(b => b.rewardType === 'revenue_share');
  const filteredBounties = filter === 'all' ? BOUNTIES :
    filter === 'live' ? liveBounties :
    revenueBounties;

  const totalEarnedFromBounties = 23; // mock

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600/20 to-amber-600/20 border-b border-gray-900">
        <div className="max-w-lg mx-auto px-4 py-5">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3 mb-1">
            <Zap className="w-7 h-7 text-amber-400" />
            <h1 className="text-xl font-bold">Bounty Board</h1>
          </div>
          <p className="text-sm text-gray-500">Cash bounties, revenue shares, and team challenges. Updated live.</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Earnings from bounties */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500">Earned from bounties this week</div>
            <div className="text-2xl font-bold text-emerald-400">${totalEarnedFromBounties}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">On top of your regular tips</div>
            <div className="text-sm text-gray-400">That's free money.</div>
          </div>
        </div>

        {/* Revenue share explainer */}
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-emerald-300">Revenue Sharing</h3>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Some bounties pay you a <span className="text-emerald-400 font-medium">percentage of the sale price</span> — not just a flat bonus.
            Sell a $60 bottle of wine on a 5% share? That's <span className="text-white font-bold">$3 in your pocket</span>, on top of the tip.
          </p>
          <p className="text-xs text-gray-500">
            The restaurant makes more revenue. You make more money. Everybody wins.
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1 border border-gray-800 w-fit">
          {[
            { key: 'all' as const, label: 'All' },
            { key: 'live' as const, label: `Live (${liveBounties.length})` },
            { key: 'revenue' as const, label: 'Rev Share' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === f.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Bounty list */}
        <div className="space-y-2">
          {filteredBounties.map((bounty) => {
            const tc = typeConfig[bounty.type];
            const rb = rewardBadge[bounty.rewardType];
            return (
              <div
                key={bounty.id}
                className={`rounded-xl border p-4 transition-all ${
                  bounty.status === 'live' ? 'bg-gray-900 border-gray-800 hover:border-gray-700' :
                  bounty.status === 'claimed' ? 'bg-gray-950 border-gray-900 opacity-60' :
                  bounty.status === 'expired' ? 'bg-gray-950 border-gray-900 opacity-40' :
                  'bg-gray-900/50 border-dashed border-gray-800'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${tc.bg} ${tc.color}`}>
                      {tc.label}
                    </span>
                    <span className={`text-[10px] font-medium ${rb.color}`}>{rb.label}</span>
                    {bounty.status === 'live' && bounty.type === 'flash' && (
                      <span className="text-[10px] text-orange-400 animate-pulse font-bold">LIVE</span>
                    )}
                    {bounty.status === 'claimed' && (
                      <span className="text-[10px] text-gray-500">Claimed by {bounty.claimedBy}</span>
                    )}
                    {bounty.status === 'expired' && (
                      <span className="text-[10px] text-gray-600">Expired</span>
                    )}
                    {bounty.status === 'upcoming' && (
                      <span className="text-[10px] text-indigo-400">Coming soon</span>
                    )}
                  </div>
                  <span className={`text-sm font-bold ${bounty.status === 'live' ? 'text-emerald-400' : 'text-gray-600'}`}>
                    {bounty.reward}
                  </span>
                </div>

                <h3 className="text-sm font-semibold text-white mb-0.5">{bounty.title}</h3>
                <p className="text-xs text-gray-500 mb-2">{bounty.description}</p>

                {bounty.progress && bounty.progress.target > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-400 rounded-full"
                        style={{ width: `${(bounty.progress.current / bounty.progress.target) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{bounty.progress.current}/{bounty.progress.target}</span>
                  </div>
                )}

                {bounty.progress && bounty.progress.target === 0 && (
                  <div className="text-xs text-emerald-400 mb-2">
                    {bounty.progress.current} sold so far = ${(bounty.progress.current * 3).toFixed(0)} earned
                  </div>
                )}

                {bounty.script && bounty.status === 'live' && (
                  <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-lg p-2.5 mt-2">
                    <div className="flex items-center gap-1 mb-1 text-[10px] text-indigo-400 font-medium">
                      <Target className="w-3 h-3" /> Suggested script
                    </div>
                    <p className="text-xs text-indigo-200 italic">{bounty.script}</p>
                  </div>
                )}

                {bounty.timeLeft && bounty.status === 'live' && (
                  <div className="flex items-center gap-1 text-xs text-gray-600 mt-2">
                    <Clock className="w-3 h-3" /> {bounty.timeLeft}
                  </div>
                )}
                {bounty.timeLeft && bounty.status === 'upcoming' && (
                  <div className="flex items-center gap-1 text-xs text-indigo-400 mt-2">
                    <Clock className="w-3 h-3" /> {bounty.timeLeft}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* How bounties work */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">How bounties work</h3>
          <div className="space-y-3 text-xs text-gray-400">
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold w-4 flex-shrink-0">1.</span>
              <span>Your restaurant sets bounties — flash challenges, kitchen pushes, and revenue shares. Some pop up mid-shift.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold w-4 flex-shrink-0">2.</span>
              <span>You see the bounty, the reward, and the exact script to use. No guessing.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold w-4 flex-shrink-0">3.</span>
              <span>Sell the item, POS verifies it automatically. Cash bonus or revenue share hits your account.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold w-4 flex-shrink-0">4.</span>
              <span>This is <span className="text-white font-medium">on top of your tips</span>. Bounty money is extra.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashBounties;
